import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Monitor, Sparkles } from 'lucide-react';
import { Suspense, useState, type ComponentType, type ReactNode } from 'react';

import { useIntl } from 'react-intl';
import { getWorkHostPort } from '../../work-home/work-home-ports';

export interface ClarificationLoginComputerProps {
  disabled: boolean;
}

/**
 * What the app supplies: the cloud resident's screen and its expanded view.
 * Both are the computer-use surface's, so they cross as render props; the
 * take-over and lightbox state stay here and are passed in.
 */
export interface ClarificationLoginComputerHost {
  /** Rendered inside this card's Suspense boundary, so it may be lazy. */
  renderLiveView: (args: { className: string }) => ReactNode;
  renderExpandButton: (onClick: () => void) => ReactNode;
  renderLightbox: (args: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    interactive: boolean;
    footer: ReactNode;
  }) => ReactNode;
}

/**
 * Answer surface for the ``login_computer`` clarification kind: the run hit
 * an app sign-in page on the cloud resident and needs the user's
 * credentials. Grok-style handoff card — an "Action needed" badge, the
 * resident's screen (view-only until the user taps "Take over", then fully
 * interactive so they sign in right here), and the shared footer's primary
 * button relabeled "I'm done" to resume the run.
 */
export const ClarificationLoginComputerView = ({
  disabled,
  renderLiveView,
  renderExpandButton,
  renderLightbox,
}: ClarificationLoginComputerProps & ClarificationLoginComputerHost): JSX.Element => {
  const intl = useIntl();
  const [takenOver, setTakenOver] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2" data-testid="clarification-login-computer">
      <div className="flex items-center gap-2">
        <Monitor className="size-3.5 text-text-secondary" />
        <TypographyCaption className="font-medium text-text-secondary">
          {intl.formatMessage({ id: 'GufTjDXPMg', defaultMessage: 'Computer' })}
        </TypographyCaption>
        <span className="ms-auto flex items-center gap-1 rounded-full bg-green-500/15 px-2 py-0.5 text-[11px] font-medium text-green-600">
          <Sparkles className="size-3" />
          {intl.formatMessage({ id: '7V6vuDWSaW', defaultMessage: 'Action needed' })}
        </span>
      </div>

      <div className="relative overflow-hidden rounded-lg border border-border bg-black">
        <Suspense fallback={<div className="aspect-video w-full bg-black" />}>
          {renderLiveView({
            className: cn('aspect-video', !takenOver && 'pointer-events-none'),
          })}
        </Suspense>
        {renderExpandButton(() => setLightboxOpen(true))}
        {!takenOver && (
          <button
            type="button"
            onClick={() => setTakenOver(true)}
            disabled={disabled}
            className="absolute inset-0 flex items-end justify-start bg-transparent p-3"
            data-testid="clarification-login-take-over-overlay"
          >
            <span className="rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-black shadow-md">
              {intl.formatMessage({ id: 'A2o+XPme5F', defaultMessage: 'Take over' })}
            </span>
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <TypographyCaption className="text-text-secondary">
          {takenOver
            ? intl.formatMessage({
                id: '6huFekBR9S',
                defaultMessage: "You're in control — sign in above, then tap “I'm done”.",
              })
            : intl.formatMessage({
                id: 'QpHHCX8VIa',
                defaultMessage: 'Tap “Take over” to sign in on your resident yourself.',
              })}
        </TypographyCaption>
        {takenOver && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTakenOver(false)}
            disabled={disabled}
            className="shrink-0"
          >
            {intl.formatMessage({ id: 'fWHvUVvowS', defaultMessage: 'Hand back' })}
          </Button>
        )}
      </div>

      {/* Semi-fullscreen presentation. Shares the card's take-over state so
          control follows the user between sizes, and take-over is available
          from the expanded view too (signing in is easier at full size). */}
      {renderLightbox({
        open: lightboxOpen,
        onOpenChange: setLightboxOpen,
        interactive: takenOver,
        footer: (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-black/80 to-transparent p-3">
            <TypographyCaption className="text-white/80">
              {takenOver
                ? intl.formatMessage({
                    id: 'H1gXCowNJr',
                    defaultMessage: "You're in control — sign in on the screen above.",
                  })
                : intl.formatMessage({
                    id: 'ZUnHZoh6Bb',
                    defaultMessage: 'Watching only — take over to sign in yourself.',
                  })}
            </TypographyCaption>
            <Button
              variant={takenOver ? 'outline' : 'default'}
              size="sm"
              onClick={() => setTakenOver((v) => !v)}
              disabled={disabled}
              className="shrink-0"
              data-testid="clarification-login-lightbox-take-over"
            >
              {takenOver
                ? intl.formatMessage({ id: 'fWHvUVvowS', defaultMessage: 'Hand back' })
                : intl.formatMessage({ id: 'A2o+XPme5F', defaultMessage: 'Take over' })}
            </Button>
          </div>
        ),
      })}
    </div>
  );
};

/** Binds the login card to the cloud resident's live view and lightbox. */
export const ClarificationLoginComputer = ({
  disabled,
}: ClarificationLoginComputerProps): JSX.Element => {
  const desktop = getWorkHostPort('desktop');
  const LiveView = desktop.CloudComputerLiveView;
  const ExpandButton = desktop.CloudComputerExpandButton;
  const Lightbox = desktop.CloudComputerLightbox as ComponentType<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    interactive: boolean;
    footer: ReactNode;
  }>;
  return (
    <ClarificationLoginComputerView
      disabled={disabled}
      renderLiveView={({ className }) => <LiveView className={className} />}
      // The host's button and lightbox load lazily, so each gets its own boundary.
      renderExpandButton={(onClick) => (
        <Suspense fallback={null}>
          <ExpandButton onClick={onClick} />
        </Suspense>
      )}
      renderLightbox={(lightbox) => (
        <Suspense fallback={null}>
          <Lightbox {...lightbox} />
        </Suspense>
      )}
    />
  );
};
